import { useState } from 'react';
import { Package } from 'lucide-react';
import { useT } from '../../i18n';
import type { Asset, AssetDraft } from '../../lib/api/assets';
import type { AssetMutationResult } from '../../state/useAssets';
import { AssetEditor, assetKindLabel, assetPriceSummary } from '../booking/AssetEditor';
import { SetupCard } from '../ui';

/**
 * THE «UTSTYR» ENTRY IN THE ORGANIZATION'S SETTINGS (the owner, 2026-10-21).
 *
 * The equipment registry — ball machines and whatever else a venue or a club
 * rents out beside the courts (docs/domains/ASSET_BOOKING.md) — was reachable
 * from the booking calendar's admin bar only. The owner wanted the
 * organization to see its equipment where it sees the rest of what it is:
 * its settings. So this draws the list, and the one button opens THE SAME
 * `AssetEditor` the calendar opens; one registry, one editor, one set of
 * words.
 *
 * Its own component for the reason `AccessSettingsCard` is: the settings
 * view is a legacy surface whose strings are not in the catalog yet, and
 * these must be. Drawn only when the shell hands the view an `equipment`
 * prop — it does so for an account holding `court.manage` here, which is what
 * the registry's writes ask for — and the server refuses everybody else
 * regardless.
 */
export interface EquipmentSettingsCardProps {
  /** Everything this organization keeps or owns — the registry as loaded. */
  assets: Asset[];
  /** The booking config — the holiday set the price-zone editor needs. */
  config?: any;
  onSave: (draft: AssetDraft & { id?: string }) => Promise<AssetMutationResult<Asset>>;
  onDelete: (id: string) => Promise<AssetMutationResult>;
}

export function EquipmentSettingsCard({
  assets,
  config = null,
  onSave,
  onDelete,
}: EquipmentSettingsCardProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // THE ORGANIZATION'S OWN first, then what it merely keeps for others — the
  // order the question «what is ours» is asked in. Inactive units stay in
  // the list: hidden from booking is not gone.
  const listed = [...assets].sort(
    (a, b) => Number(b.ownedHere) - Number(a.ownedHere) || a.sortOrder - b.sortOrder,
  );
  return (
    <SetupCard label={t('clubSettings.equipment.title')}>
      {/* Stacked, the button under the text — see AccessSettingsCard. */}
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-2">{t('clubSettings.equipment.lead')}</p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-outline text-xs self-start inline-flex items-center gap-1.5"
          data-testid="club-settings-open-equipment"
        >
          <Package size={12} /> {t('clubSettings.equipment.open')}
        </button>
      </div>
      {listed.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3" data-testid="club-settings-equipment-none">
          {t('booking.assets.empty')}
        </p>
      ) : (
        <ul className="mt-3 rounded-lg border border-line overflow-hidden">
          {listed.map((a, i) => (
            <li
              key={a.id}
              data-testid={`club-settings-equipment-${a.id}`}
              className={`flex items-start justify-between gap-3 px-3 py-2 ${
                i > 0 ? 'border-t border-line-soft' : ''
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ink truncate">{a.name}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                    {assetKindLabel(t, a.kind)}
                  </span>
                  {!a.active && (
                    <span className="text-[10px] font-mono uppercase tracking-wider rounded px-1 py-0.5 bg-amber-100 text-amber-800">
                      {t('booking.assets.inactive')}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-ink-3 flex flex-wrap gap-x-3">
                  <span>{assetPriceSummary(t, a)}</span>
                  {!a.ownedHere && a.ownerName && (
                    <span>{t('booking.assets.ownedBy', { name: a.ownerName })}</span>
                  )}
                  {!a.atThisVenue && a.venueName && (
                    <span>{t('booking.assets.atVenue', { name: a.venueName })}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <AssetEditor
          assets={assets}
          config={config}
          onClose={() => setOpen(false)}
          onSave={onSave}
          onDelete={onDelete}
        />
      )}
    </SetupCard>
  );
}
